/**
 * PQL → plain English — the deterministic prose strip under the statement bar
 * (PRD §3.2). No LLM involved: this rendering is derived mechanically from the
 * AST, so it is always true. An LLM may later produce friendlier phrasings,
 * but this is the reference rendering.
 */

import { isCombinator, type FilterRule, type PqlCatalog, type PqlQuery, type QueryNode, type SortSpec } from './types.ts';

function ruleLabel(rule: FilterRule): string {
  return rule.label || rule.field;
}

function quoted(value: unknown): string {
  return `“${String(value)}”`;
}

function leafEnglish(rule: FilterRule, negated: boolean): string {
  const label = ruleLabel(rule);
  switch (rule.operator) {
    case 'in': {
      const values = Array.isArray(rule.value) ? (rule.value as string[]) : [String(rule.value)];
      const shown = rule.displayValue || values.join(', ');
      if (values.length === 1) return `${label} is ${negated ? 'not ' : ''}${shown}`;
      const list = rule.displayValue ? rule.displayValue.split(', ') : values;
      return `${label} is ${negated ? 'none of' : 'one of'} ${list.join(' or ')}`;
    }
    case 'eq':
      return `${label} is ${negated ? 'not ' : ''}${quoted(rule.value)}`;
    case 'text':
      return negated
        ? `${label} does not contain ${quoted(rule.value)}`
        : `${label} contains ${quoted(rule.value)}`;
    case 'boolean': {
      const yes = Boolean(rule.value) !== negated;
      return `${label}: ${yes ? 'yes' : 'no'}`;
    }
    case 'range': {
      const [a, b] = rule.value as [number | null, number | null];
      const min = typeof a === 'number' ? a : -Infinity;
      const max = typeof b === 'number' ? b : Infinity;
      if (min === max) return `${label} is ${negated ? 'not ' : ''}${min}`;
      if (max === Infinity) return negated ? `${label} is less than ${min}` : `${label} is at least ${min}`;
      if (min === -Infinity) return negated ? `${label} is more than ${max}` : `${label} is at most ${max}`;
      return `${label} is ${negated ? 'not ' : ''}between ${min} and ${max}`;
    }
    case 'date-range': {
      const [start, end] = rule.value as [string, string];
      if (start && end) {
        if (start === end) return `${label} is ${negated ? 'not ' : ''}on ${start}`;
        return `${label} is ${negated ? 'not ' : ''}between ${start} and ${end}`;
      }
      if (start) return negated ? `${label} is before ${start}` : `${label} is on or after ${start}`;
      if (end) return negated ? `${label} is after ${end}` : `${label} is on or before ${end}`;
      return `${label} has no date`;
    }
    case 'empty':
      return `${label} is ${negated ? 'not ' : ''}empty`;
    case 'gt':
      return negated ? `${label} is not more than ${rule.value}` : `${label} is more than ${rule.value}`;
    case 'lt':
      return negated ? `${label} is not less than ${rule.value}` : `${label} is less than ${rule.value}`;
    case 'date-after':
      return negated ? `${label} is not after ${rule.value}` : `${label} is after ${rule.value}`;
    case 'date-before':
      return negated ? `${label} is not before ${rule.value}` : `${label} is before ${rule.value}`;
    default:
      return `${label} matches ${quoted(rule.value)}`;
  }
}

function nodeEnglish(node: QueryNode, parent: 'and' | 'or' | null): string {
  if (!isCombinator(node)) return leafEnglish(node, false);

  if (node.op === 'not') {
    if (!isCombinator(node.child)) return leafEnglish(node.child, true);
    return `not (${nodeEnglish(node.child, null)})`;
  }

  const joiner = node.op === 'and' ? ' and ' : ' or ';
  const joined = node.children.map((c: QueryNode) => nodeEnglish(c, node.op)).join(joiner);
  // Parenthesize mixed nesting so the prose can't be misread.
  if (parent && parent !== node.op) return `(${joined})`;
  return joined;
}

export function toEnglish(query: PqlQuery, catalog?: PqlCatalog): string {
  const parts: string[] = [];
  parts.push(query.where ? `Items where ${nodeEnglish(query.where, null)}` : 'All items');
  if (query.orderBy.length > 0) {
    const sorts = query.orderBy.map((s: SortSpec) => {
      const label = catalog?.resolveField(s.field)?.label ?? s.field;
      return `${label} ${s.direction === 'asc' ? 'ascending' : 'descending'}`;
    });
    parts.push(`sorted by ${sorts.join(', then ')}`);
  }
  return parts.join(', ');
}
